import clsx from 'clsx';
import { useUnit } from 'effector-react';
import _ from 'lodash';
import $$api from 'models/api';
import $$ep from 'models/ep';
import navigate from 'models/router-gate';
import { $global, storage } from 'models/store-global';
import { $modals } from 'models/store-modals';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import './ProgressPage.scss';

import Dropdown from 'components/StyledTemplates/Dropdown';
import { Button } from 'components/StyledTemplates/StyledTemplates';
import { Option } from 'components/Table/Table';
import AdvisorLogo from 'components/pages/Dashboard/common/AdvisorLogo';
import { MoreButton } from 'components/pages/Dashboard/common/CommunityPageStyles';
import EpProgress from 'components/pages/Dashboard/common/EpProgress';
import LearnCenter from 'components/pages/Dashboard/common/LearnCenter';
import PlanTitle from 'components/pages/Dashboard/common/PlanTitle';
import YourResources from 'components/pages/Dashboard/common/YourResources';
import AdditionalProducts from 'components/pages/Dashboard/complete/AdditionalProducts';
import SectionCard from 'components/pages/Dashboard/progress/SectionCard';
import LoadingPage from 'components/pages/LoadingPage/LoadingPage';

import { QUESTIONS_PROGRESS_PERCENT } from 'constants/dashboard';

import AlertsService from 'services/AlertsService';
import DashboardService from 'services/DashboardService';
import EstatePlanService from 'services/EstatePlanService';
import ParticipantsService from 'services/ParticipantsService';
import QuestionsService from 'services/QuestionsService';
import SectionsService from 'services/SectionsService';
import { isna } from 'utils/common-helper';

import useDocumentTitle from 'hooks/useDocumentTitle';
import useResources from 'hooks/useResources';

import { MyStore } from 'types/Store';
import { type Document } from 'types/zod/document';
import { type Section } from 'types/zod/section';

const service = new QuestionsService();

const ProgressPage = () => {
    const { t } = useResources(['common', 'ep']);

    const [loaded, setLoaded] = useState(false);
    const [processedSections, setProcessedSections] = useState([] as Section[]);
    const [isFinished, setIsFinished] = useState(false);
    const [inProgressSections, setInProgressSections] = useState([] as Section[]);
    const [upNextSections, setUpNextSections] = useState([] as Section[]);
    const [completedSections, setCompletedSections] = useState([] as Section[]);
    const [sectionProgress, setSectionProgress] = useState(0);
    const [isUpNextCollapsed, setUpNextCollapsed] = useState(true);

    const estatePlan = useUnit($$ep.stores.$epQInstance);

    const shouldStartAssessment = useUnit($global.map((state) => state.startAssessment));

    const isModalAssessmentOpened = useUnit($modals.map((modals) => modals.MODAL_ASSESSMENT?.isOpened));

    useEffect(() => {
        (async () => {
            if (!estatePlan) {
                return;
            }
            const { questionnaire_type_code, state_code, questionnaire_instance_id } = estatePlan;
            if (![questionnaire_type_code, state_code, questionnaire_instance_id].map((i: any) => !i).includes(true)) {
                const questionnairePromise = $$api.questionnaires.getQuestionnaireFx({
                    questionnaireType: questionnaire_type_code,
                    state: state_code,
                });
                const commonAnswersPromise = $$api.answers.getCommonAnswersFx(questionnaire_type_code);
                const answersPromise = $$api.answers.getAnswersFx({ id: questionnaire_instance_id });
                const isPaymentOkPromise = $$api.qInstances.isPaymentOkFx(questionnaire_instance_id);
                const summaryPromise = $$api.qInstances.getSummaryFx({ id: questionnaire_instance_id });
                try {
                    const response = await Promise.all([
                        questionnairePromise,
                        commonAnswersPromise,
                        answersPromise,
                        isPaymentOkPromise,
                        summaryPromise,
                    ]);
                    const [questionnaire, commonAnswers, answers] = response;
                    service.enhanceQuestionWithPathsAndNames(questionnaire);
                    const sections = SectionsService.getSections(questionnaire);
                    commonAnswers.q_instance = estatePlan;
                    const progress = service.getQuestionnaireProgress(
                        questionnaire,
                        questionnaire,
                        answers,
                        commonAnswers,
                    );
                    const processedSections = SectionsService.calculateStatuses(
                        questionnaire,
                        sections,
                        _.assign(_.cloneDeep(answers) || {}, commonAnswers),
                    ).filter((section) => !(section.name === 'payment' && section.status !== 'inProgress'));
                    const { isFinished = false } = progress;
                    setIsFinished(isFinished);
                    setProcessedSections(processedSections);
                } catch (error) {
                    AlertsService.handleError(error);
                }
            }
        })();
    }, [estatePlan?.state_code, estatePlan?.questionnaire_instance_id]);

    useEffect(() => {
        if (!estatePlan) {
            return;
        }
        if (shouldStartAssessment) {
            storage.set((store): MyStore => ({ ...store, startAssessment: false }));
            if (!estatePlan?.status) {
                DashboardService.startAssessment();
            } else {
                navigate(
                    `/document/${estatePlan.questionnaire_type_code}/${estatePlan.state_code}/${estatePlan?.questionnaire_instance_id}/continue`,
                );
            }
        } else {
            setLoaded(true);
        }
    }, [shouldStartAssessment, estatePlan]);

    const community = useUnit($global.map((state) => state.community));
    const summary = useUnit(
        $global.map((state) => state?.qInstanceSummary?.[estatePlan?.questionnaire_instance_id || -1] || null),
    );

    const isParticipantsInfoComplete = summary
        ? !ParticipantsService.shouldFillInfo(ParticipantsService.getParticipantsFromSummary(summary, {}))
        : false;

    useEffect(() => {
        if (!processedSections.length) {
            return;
        }
        const sections = processedSections.slice();
        const participantInfoSectionStatus = isFinished
            ? isParticipantsInfoComplete
                ? 'complete'
                : 'inProgress'
            : 'notStarted';
        sections.push({
            name: 'participants_info_section',
            status: participantInfoSectionStatus,
            questionForCondition: {},
            questionnaire: {},
        });

        const numberOfCompleteSections = (sections || []).filter((i: any) => i?.status === `complete`).length;
        const totalNumber = (sections || []).length;
        const sectionProgress = numberOfCompleteSections / totalNumber;

        const inProgressSections = sections.filter((section: Section) => section.status === 'inProgress');
        const upNextSections = sections.filter((section: Section) => section.status === 'notStarted');
        const completedSections = sections.filter((section: Section) => section.status === 'complete');

        const estateSummarySectionStatus = isFinished && isParticipantsInfoComplete ? 'inProgress' : 'notStarted';
        (estateSummarySectionStatus === 'inProgress' ? inProgressSections : upNextSections).push({
            name: 'estate_summary_section',
            status: estateSummarySectionStatus,
            questionForCondition: {},
            questionnaire: {},
        });

        setInProgressSections(inProgressSections.slice());
        setUpNextSections(upNextSections.slice());
        setCompletedSections(completedSections.slice());
        setSectionProgress(sectionProgress);
    }, [processedSections, isFinished, isParticipantsInfoComplete]);

    const will = (community?.docs || []).find((doc: Document) => doc.document_type_code === 'Will');

    const shouldShowInProgress = inProgressSections.length || will;
    const isUpdateWithAllCompleted = will && !inProgressSections.length && !upNextSections.length;

    useEffect(() => {
        if (!isna(estatePlan?.questionnaire_instance_id)) {
            loadData();
        }
    }, [JSON.stringify(estatePlan?.questionnaire_instance_id)]);

    const loadData = async () => {
        if (estatePlan?.questionnaire_instance_id) {
            await Promise.all([
                $$api.qInstances.getDocumentsForQInstanceFx(estatePlan?.questionnaire_instance_id),
                $$api.qInstances.getDocumentTypesForQInstanceFx(estatePlan?.questionnaire_instance_id),
            ]);
        }
    };

    useDocumentTitle(`In progress`);

    const revertBackToOriginal = async () => {
        if (will && (estatePlan?.date_modified || 0) > will.last_modified_date) {
            DashboardService.requestEstatePlanCancel();
        } else {
            await DashboardService.cancelEstatePlan(estatePlan?.questionnaire_instance_id || -1);
        }
    };

    const questionnaireLink = `/document/${estatePlan?.questionnaire_type_code}/${estatePlan?.state_code}/${estatePlan?.questionnaire_instance_id}`;

    const resume = () => {
        storage.set((store): MyStore => {
            const { stopAtParticipantsInfo, ...navigation } = store.navigation || {};
            return { ...store, navigation };
        });
        navigate(`${questionnaireLink}/continue`);
    };

    const goToParticipantsInfo = () => {
        storage.set(
            (store): MyStore => ({
                ...store,
                navigation: {
                    ...(store.navigation || {}),
                    stopAtParticipantsInfo: true,
                },
            }),
        );
        navigate(`/participants-info`);
    };

    const scrollToCompletedSections = () => {
        const scrollElement = document.querySelector('.completed-sections');
        if (scrollElement) {
            scrollElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    };

    const handleInProgressButtonClick = (sectionName: string) => {
        if (sectionName === 'participants_info_section') {
            goToParticipantsInfo();
        } else {
            resume();
        }
    };

    const shouldShowDiagram = (sectionName: string): boolean => {
        if (sectionName === 'specific_distros_section') {
            return !!EstatePlanService.getAnswerByPath('specific_distros.specific_distros_section_diagram');
        } else if (sectionName === 'poa_section') {
            return !!EstatePlanService.getAnswerByPath('health_decisions.poa_section_diagram');
        } else if (sectionName === 'health_decisions_section') {
            return !!EstatePlanService.getAnswerByPath('health_decisions.health_decisions_section_diagram');
        }
        return true;
    };

    const handleCompleteButtonClick = async (sectionName: string) => {
        if (sectionName === 'getting_started_section') {
            navigate(`${questionnaireLink}/getting_started_section`);
        } else if (sectionName === 'estate_summary_section') {
            resume();
        } else if (sectionName === 'participants_info_section') {
            goToParticipantsInfo();
        } else if (shouldShowDiagram(sectionName)) {
            await DashboardService.showDiagram(sectionName);
        } else {
            navigate(`${questionnaireLink}/${sectionName}`);
        }
    };

    if (!loaded) {
        return <LoadingPage />;
    }

    const EpButtons = (
        <>
            {will && (
                <Button
                    data-qaid="revert_back_to_original_button"
                    variant="outline-secondary"
                    onClick={revertBackToOriginal}
                    className="revert-button text-nowrap"
                >
                    {t('EPCard.revertBackToOriginal')}
                </Button>
            )}
            <Dropdown
                toggle={<MoreButton data-qaid="three_dots_menu_toggle_in_progress" />}
                menu={
                    <>
                        <Option data-qaid="resume_button" onClick={resume}>
                            {t('EPCard.resume')}
                        </Option>
                        <Option data-qaid="delete_ep_button" onClick={DashboardService.requestEstatePlanDelete}>
                            {t('EPCard.delete')}
                        </Option>
                    </>
                }
            />
        </>
    );

    return (
        <div className="progress-page">
            {isModalAssessmentOpened && <TempUserEmptyPlaceholder />}
            {!isModalAssessmentOpened && (
                <>
                    <div className="d-flex gap-4 align-items-center mb-4">
                        <EpProgress
                            isUpdate={!!will && sectionProgress === 1}
                            progress={QUESTIONS_PROGRESS_PERCENT * sectionProgress}
                        />
                        <div className="flex-grow-1">
                            <PlanTitle />
                        </div>
                        <div className="d-none d-md-block">
                            <AdvisorLogo />
                        </div>
                    </div>
                    {shouldShowInProgress && (
                        <>
                            <div className="section-group-header">
                                <h4 className="serif text-dark w-full">
                                    {will
                                        ? t('newDashboardPage.inProgressPage.updateInProgress')
                                        : t('newDashboardPage.inProgressPage.inProgress')}
                                </h4>
                                {EpButtons}
                            </div>
                            <div className="sections">
                                {inProgressSections.map((section: Section, index: number) => {
                                    return (
                                        <SectionCard
                                            key={index}
                                            name={section.name}
                                            status={section.status}
                                            onButtonClick={handleInProgressButtonClick}
                                        />
                                    );
                                })}
                            </div>
                            {!isUpNextCollapsed && !!completedSections.length && (
                                <div
                                    className="section-group-link"
                                    onClick={() => {
                                        scrollToCompletedSections();
                                    }}
                                >
                                    {t('newDashboardPage.inProgressPage.seeCompletedSections')}
                                </div>
                            )}
                        </>
                    )}

                    {!!upNextSections.length && (
                        <>
                            <div className="section-group-header">
                                <h4 className="serif text-black">{t('newDashboardPage.inProgressPage.upNext')}</h4>
                            </div>
                            <div
                                className={clsx(
                                    'sections',
                                    !(isUpNextCollapsed && upNextSections.length > 1) && 'mb-3',
                                )}
                            >
                                {isUpNextCollapsed && upNextSections.length > 1 ? (
                                    <>
                                        <div className="with-background-card">
                                            <SectionCard
                                                name={upNextSections[0].name}
                                                status={upNextSections[0].status}
                                            />
                                        </div>
                                        <div
                                            className="section-group-link"
                                            onClick={() => {
                                                setUpNextCollapsed(false);
                                            }}
                                        >
                                            {t('newDashboardPage.inProgressPage.seeAllSections')}
                                        </div>
                                    </>
                                ) : (
                                    upNextSections.map((section: Section) => {
                                        return (
                                            <SectionCard
                                                key={section.name}
                                                name={section.name}
                                                status={section.status}
                                            />
                                        );
                                    })
                                )}
                            </div>
                        </>
                    )}

                    {!!completedSections.length && !isUpdateWithAllCompleted && (
                        <>
                            <div className="section-group-header completed-sections">
                                <h4 className="serif text-black">{t('newDashboardPage.inProgressPage.completed')}</h4>
                                {!shouldShowInProgress && EpButtons}
                            </div>
                            <div className="sections">
                                {completedSections.map((section: Section) => {
                                    return (
                                        <SectionCard
                                            key={section.name}
                                            name={section.name}
                                            status={section.status}
                                            onButtonClick={handleCompleteButtonClick}
                                        />
                                    );
                                })}
                            </div>
                        </>
                    )}

                    {isUpdateWithAllCompleted && (
                        <div className="sections">
                            {completedSections.map((section: Section) => {
                                return (
                                    <SectionCard
                                        key={section.name}
                                        name={section.name}
                                        status={section.status}
                                        onButtonClick={handleCompleteButtonClick}
                                    />
                                );
                            })}
                        </div>
                    )}

                    {!!will && (
                        <div className="text-right mt-3">
                            <Button
                                data-qaid="finish_update_button"
                                variant="primary_action"
                                onClick={() => {
                                    DashboardService.requestEstatePlanFinishUpdate();
                                }}
                            >
                                {t('EPCard.finishUpdate')}
                            </Button>
                        </div>
                    )}
                </>
            )}

            <div className={clsx({ 'mt-3': true })}>
                <AdditionalProducts />
            </div>

            <div className={clsx({ 'mt-5': !will, 'mt-3': !!will })}>
                <YourResources />
            </div>

            <div className="mt-5 mb-2">
                <LearnCenter articles={t(`newDashboardPage.learnCenter.articles`, { returnObjects: true })} />
            </div>
        </div>
    );
};

const TempUserEmptyPlaceholder = styled.div`
    width: 100%;
    height: 350px;
`;

export default ProgressPage;
